/**
 * CardDetail - Reusable card component for detail page sections
 * Based on CardActivities.tsx and Contracts section in PageActivity.tsx
 * Used for: Sub-Activities, Contracts, Activities on work plan, etc.
 *
 * This is a composable component that supports:
 * - Custom header actions (buttons, checkboxes, etc.)
 * - Custom list item renderers
 * - Different visual styles (solid border, dashed border)
 * - Empty states with optional action buttons
 */

// react
import { ReactNode, useMemo, useState } from 'react';

// mui
import { alpha, Box, Button, Checkbox, Divider, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import {
	Add as IconAdd,
	Checklist as IconChecklist,
	Close as IconClose,
} from '@mui/icons-material';
import { SvgIconComponent } from '@mui/icons-material';

// design system
import { radius } from '../../../utils/designSystem';

// data
import { GRADIENTS } from '../../../data/colors';

// Types for color variants
type ColorVariant = 'activity' | 'workPlan' | 'contract' | 'condition' | 'financial' | 'payment';

// Generic type for list items
interface CardDetailProps<T> {
	/** The icon component to display in the header (defaults to icon prop if not provided) */
	headerIcon?: SvgIconComponent;
	/** The icon component to display (used for list items, or header if headerIcon not provided) */
	icon: SvgIconComponent;
	/** Card title */
	title: string;
	/** Card subtitle (e.g., "3 activities", "0 contracts") */
	subtitle?: string;
	/** Color variant for styling */
	variant?: ColorVariant;
	/** Handler for primary add button click (shows + icon in header) */
	onAdd?: () => void;
	/** Tooltip text for add button */
	tooltipAdd?: string;
	/** Message to display when content is empty */
	emptyMessage?: string;
	/** Label for empty state action button */
	emptyActionLabel?: string;
	/** Whether to use dashed border style (for "add" type cards) */
	dashed?: boolean;
	/** Override color for the header icon circle (defaults to variant color) */
	iconColor?: string;
	/** Whether to hide the header completely */
	hideHeader?: boolean;

	// === Composable Props ===

	/** Custom header actions - replaces default add button. Use for complex actions like "Add Existing" + "+" buttons */
	headerActions?: ReactNode;

	/**
	 * Items to render in the list. When provided with renderItem, enables the built-in list rendering.
	 * If not using items/renderItem, use children prop for full custom content.
	 */
	items?: T[];

	/**
	 * Custom render function for each list item.
	 * @param item - The item to render
	 * @param index - The index of the item
	 * @returns ReactNode
	 */
	renderItem?: (item: T, index: number) => ReactNode;

	/** Whether to show dividers between list items */
	showDividers?: boolean;

	/**
	 * Fully custom content (children). Use when you need complete control over the card content.
	 * This is used instead of items/renderItem when you need more flexibility.
	 */
	children?: ReactNode;

	/** Content to render below the list (e.g., "Add Selected" button) */
	footerContent?: ReactNode;

	/** Custom padding for the content area */
	contentPadding?: boolean;

	/**
	 * Optional theme color for the card. When provided:
	 * - Card body gets a very light tint (6% opacity)
	 * - Card header gets a slightly darker tint (12% opacity)
	 * - Borders adapt to the theme color
	 * Pass any CSS color string (e.g., '#2563eb', 'rgb(37,99,235)', etc.)
	 * Omit to keep the default all-white appearance.
	 */
	themeColor?: string;

	/** Enable select mode toggle in the header */
	selectable?: boolean;
	/** Actions available when items are selected (e.g., delete) */
	selectionActions?: Array<{
		label: string;
		icon: SvgIconComponent;
		onClick: (selectedItems: T[]) => void;
		color?: string;
	}>;
	/** Get unique key for an item (required when selectable is true) */
	getItemKey?: (item: T) => string | number;
}

export const CardDetail = <T,>({
	headerIcon,
	icon: Icon,
	title,
	subtitle,
	variant = 'activity',
	onAdd,
	tooltipAdd = 'Add',
	emptyMessage = 'No items found',
	emptyActionLabel,
	dashed = false,
	iconColor,
	hideHeader = false,
	headerActions,
	items,
	renderItem,
	showDividers = true,
	children,
	footerContent,
	contentPadding = false,
	themeColor,
	selectable,
	selectionActions,
	getItemKey,
}: CardDetailProps<T>) => {

	// Get colors based on variant
	const colors = GRADIENTS[variant] || GRADIENTS.activity;
	const headerIconColor = iconColor || colors.icon;

	// Determine if content is empty
	const isEmpty = items ? items.length === 0 : !children;

	// Use headerIcon if provided, otherwise fall back to icon
	const HeaderIcon = headerIcon || Icon;

	// Select mode state
	const [selectMode, setSelectMode] = useState(false);
	const [selectedKeys, setSelectedKeys] = useState<Set<string | number>>(new Set());

	const handleToggleSelectMode = () => {
		if (selectMode) {
			setSelectedKeys(new Set());
		}
		setSelectMode(!selectMode);
	};

	const handleToggleItem = (key: string | number) => {
		setSelectedKeys((prev) => {
			const next = new Set(prev);
			if (next.has(key)) {
				next.delete(key);
			} else {
				next.add(key);
			}
			return next;
		});
	};

	const selectedItems = useMemo(() => {
		if (!items || !getItemKey) return [] as T[];
		return items.filter((item) => selectedKeys.has(getItemKey(item)));
	}, [items, getItemKey, selectedKeys]);

	const handleSelectionAction = (actionFn: (items: T[]) => void) => {
		actionFn(selectedItems);
		setSelectMode(false);
		setSelectedKeys(new Set());
	};

	return (
		<Paper
			elevation={0}
			sx={{
				bgcolor: '#ffffff',
				borderRadius: radius.sm,
				border: dashed ? '1.5px dashed #d1d5db' : '1px solid #e5e7eb',
				borderLeft: dashed
					? undefined
					: themeColor
						? `4px solid ${themeColor}`
						: '1px solid #e5e7eb',
				overflow: 'hidden',
			}}>
			{/* Header */}
			{!hideHeader && (
				<Box
					sx={{
						background: '#fafafa',
						borderBottom: '1px solid #f0f0f0',
						px: 2,
						py: 1.5,
					}}>
					{selectMode ? (
						<Stack direction="row" alignItems="center" spacing={1} sx={{ minHeight: 34 }}>
							<Tooltip title="Cancel selection">
								<IconButton
									size="small"
									onClick={handleToggleSelectMode}
									sx={{
										color: '#6b7280',
										padding: '4px',
										'&:hover': { bgcolor: alpha('#6b7280', 0.08) },
									}}>
									<IconClose sx={{ fontSize: 18 }} />
								</IconButton>
							</Tooltip>
							<Typography
								noWrap
								sx={{
									flex: 1,
									color: '#111827',
									fontSize: '1.5rem',
									fontWeight: 600,
								}}>
								{selectedKeys.size} selected
							</Typography>
							{selectionActions?.map((action, i) => (
								<Tooltip key={i} title={action.label}>
									<span>
										<IconButton
											size="small"
											disabled={selectedKeys.size === 0}
											onClick={() => handleSelectionAction(action.onClick)}
											sx={{
												border: '1px solid',
												borderColor:
													selectedKeys.size === 0
														? '#d1d5db'
														: alpha(action.color || '#ef4444', 0.5),
												borderRadius: '6px',
												padding: '4px',
												color:
													selectedKeys.size === 0
														? '#9ca3af'
														: action.color || '#ef4444',
												'&:hover': {
													bgcolor: alpha(action.color || '#ef4444', 0.08),
													borderColor: action.color || '#ef4444',
												},
											}}>
											<action.icon sx={{ fontSize: 18 }} />
										</IconButton>
									</span>
								</Tooltip>
							))}
						</Stack>
					) : (
						<Stack direction="row" alignItems="center" spacing={1.5}>
							{/* Icon Box */}
							<Box
								sx={{
									alignItems: 'center',
									bgcolor: alpha(headerIconColor, 0.12),
									borderRadius: '50%',
									display: 'flex',
									height: 34,
									justifyContent: 'center',
									width: 34,
									flexShrink: 0,
								}}>
								<HeaderIcon sx={{ fontSize: 18, color: headerIconColor }} />
							</Box>

							{/* Title & Subtitle */}
							<Box sx={{ flex: 1, minWidth: 0 }}>
								<Typography
									sx={{
										color: '#111827',
										fontSize: '1.75rem',
										fontWeight: 700,
										lineHeight: 1.3,
									}}>
									{title}
								</Typography>
								{subtitle && (
									<Typography
										sx={{
											color: '#6b7280',
											fontSize: '1.35rem',
											fontWeight: 400,
											lineHeight: 1.4,
										}}>
										{subtitle}
									</Typography>
								)}
							</Box>

							{/* Header Actions */}
							<Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
								{selectable && items && items.length > 0 && (
									<Tooltip title="Select">
										<IconButton
											size="small"
											onClick={handleToggleSelectMode}
											sx={{
												color: '#6b7280',
												border: '1px solid',
												borderColor: '#d1d5db',
												borderRadius: '6px',
												padding: '4px',
												'&:hover': {
													bgcolor: alpha(colors.icon, 0.08),
													borderColor: colors.icon,
													color: colors.icon,
												},
											}}>
											<IconChecklist sx={{ fontSize: 18 }} />
										</IconButton>
									</Tooltip>
								)}
								{headerActions ? (
									headerActions
								) : onAdd ? (
									<Tooltip title={tooltipAdd}>
										<IconButton
											size="small"
											onClick={onAdd}
											sx={{
												color: colors.icon,
												border: '1px solid',
												borderColor: alpha(colors.icon, 0.5),
												borderRadius: '6px',
												padding: '4px',
												'&:hover': {
													bgcolor: alpha(colors.icon, 0.08),
													borderColor: colors.icon,
												},
											}}>
											<IconAdd sx={{ fontSize: 18 }} />
										</IconButton>
									</Tooltip>
								) : null}
							</Stack>
						</Stack>
					)}
				</Box>
			)}

			{/* Content */}
			<Box sx={contentPadding ? { px: 2, py: 1.5 } : {}}>
				{isEmpty ? (
					<Box sx={{ py: 3, px: 2, textAlign: 'center' }}>
						<Typography
							sx={{
								color: '#9ca3af',
								fontStyle: 'italic',
								fontSize: '1.35rem',
							}}>
							{emptyMessage}
						</Typography>
						{onAdd && emptyActionLabel && (
							<Button
								onClick={onAdd}
								size="small"
								startIcon={<IconAdd sx={{ fontSize: 16 }} />}
								sx={{
									mt: 1.5,
									textTransform: 'none',
									fontWeight: 600,
									fontSize: '1.35rem',
									color: colors.icon,
									'&:hover': {
										bgcolor: alpha(colors.icon, 0.08),
									},
								}}>
								{emptyActionLabel}
							</Button>
						)}
					</Box>
				) : items && renderItem ? (
					// Render items using the provided renderItem function
					<Stack
						spacing={0}
						divider={showDividers ? <Divider sx={{ borderColor: '#f3f4f6' }} /> : undefined}>
						{items.map((item, index) => {
							const key = getItemKey ? getItemKey(item) : index;
							if (selectMode) {
								return (
									<Stack
										key={String(key)}
										direction="row"
										alignItems="center"
										onClick={() => handleToggleItem(key)}
										sx={{
											cursor: 'pointer',
											bgcolor: selectedKeys.has(key)
												? alpha(colors.icon, 0.06)
												: 'transparent',
											transition: 'background-color 0.15s ease',
											'&:hover': {
												bgcolor: selectedKeys.has(key)
													? alpha(colors.icon, 0.08)
													: alpha(colors.icon, 0.04),
											},
										}}>
										<Checkbox
											checked={selectedKeys.has(key)}
											size="small"
											tabIndex={-1}
											sx={{ ml: 0.5, flexShrink: 0 }}
										/>
										<Box sx={{ flex: 1, minWidth: 0, pointerEvents: 'none' }}>
											{renderItem(item, index)}
										</Box>
									</Stack>
								);
							}
							return renderItem(item, index);
						})}
					</Stack>
				) : (
					// Render children for full custom content
					children
				)}
			</Box>

			{/* Footer Content (e.g., "Add Selected" button) */}
			{footerContent && <Box>{footerContent}</Box>}
		</Paper>
	);
};
